import React, { FC, useEffect, useMemo, useState } from "react";

import { Row } from "react-bootstrap";

import {
  type ArtCentreArtistFragment,
  ParticipantStatus,
  type RecipientRelationshipInput,
  SortDirection,
  useBatchUpsertRecipientRelationshipMutation,
  useListArtCentreArtistQuery,
} from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import AddEditNewArtistModal from "@components/account_profile/account_relationships/AddEditNewArtistModal";
import { Loading } from "@components/loading";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";
import { formatDateStringLong } from "@util/index";

import { Pager } from "../../table/Pager";
import DetailsRow from "../DetailsRow";

interface ArtCentreArtistsProps {
  participantId: number | undefined;
  isArtCentre: boolean;
  isCMO: boolean;
  isAgent: boolean;
  participantStatus?: string;
}

const getAssociatedArtistsHeader = () => (
  <>
    <h5>Represented Artists</h5>
  </>
);

export const ArtCentreArtists: FC<ArtCentreArtistsProps> = ({
  participantId,
  isArtCentre,
  isCMO,
  isAgent,
  participantStatus,
}) => {
  const { auth } = useAuthenticationContext();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<ArtCentreArtistFragment | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<"error" | "success" | "info">("info");

  const isVAOrMs = useIsAuthUserAllowed([Role.VisualArtistAdmin, Role.MemberServices]);
  const isArtCentreOrCMOOrAgent = isArtCentre || isCMO || isAgent;

  const pageSize = 10;
  const { data, loading, refetch } = useListArtCentreArtistQuery({
    variables: {
      input: {
        filter_by: [{ field: "recipient_id", eq: participantId?.toString() }],
        limit: pageSize,
        page,
        sort_by: { direction: SortDirection.DESCENDING, field: "start_date" },
      },
    },
    skip: !participantId || !isArtCentreOrCMOOrAgent,
  });

  const [addOrUpdateArtistMutation] = useBatchUpsertRecipientRelationshipMutation();

  const artists = data?.listArtCentreArtist?.results || [];

  useEffect(() => {
    if (data) {
      setTotalPages(data?.listArtCentreArtist?.total_pages || 0);
      setTotalItems(data?.listArtCentreArtist.total_items || 0);
    }
  }, [data]);

  const shouldDisplayAccordionContent = useMemo(() => isArtCentreOrCMOOrAgent, [isArtCentreOrCMOOrAgent]);

  const handleAddArtistClick = () => {
    setSelectedArtist(null);
    setIsModalOpen(true);
  };

  const handleEditArtistClick = (artist: ArtCentreArtistFragment) => {
    setSelectedArtist(artist);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedArtist(null);
  };

  const handleArtistSubmit = async (recipientInput: RecipientRelationshipInput) => {
    try {
      const response = await addOrUpdateArtistMutation({
        variables: {
          relationships: [
            {
              ...recipientInput,
            },
          ],
        },
      });
      const upsertResponses = response.data?.batchUpsertRecipientRelationship?.recipient_upsert_responses;

      if (upsertResponses?.every((res) => res.success)) {
        setToastMessage(selectedArtist ? "Artist updated successfully" : "New artist added successfully");
        setToastType("info");
        setShowToast(true);
        refetch();
        handleModalClose();
      } else {
        const failedMessages = upsertResponses
          ?.filter((res) => !res.success)
          .map((res) => res.message)
          .join(", ");
        throw new Error(failedMessages);
      }
    } catch (error) {
      if (error instanceof Error) {
        setToastMessage(`Error ${selectedArtist ? "updating" : "adding"} artist: ${error.message}`);
        setToastType("error");
        setShowToast(true);
      } else {
        throw error;
      }
    }
  };

  return (
    <>
      {loading && <Loading />}
      <CustomAccordion
        accordionKey={"associatedArtists"}
        contrastOpenStyle={false}
        header={getAssociatedArtistsHeader()}
        defaultActiveKey={"associatedArtists"}
      >
        <ProtectedComponent requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]} auth={auth}>
          {(participantStatus === ParticipantStatus.ACTIVE ||
            participantStatus === ParticipantStatus.PENDING_APPROVAL) && (
            <div style={{ marginLeft: "1.5rem" }} className="mb-3">
              <IconLink src={PenWriteEditIcon} text="Add Artist" onClick={handleAddArtistClick} />
            </div>
          )}
        </ProtectedComponent>
        {shouldDisplayAccordionContent && artists.length === 0 && <p>No associated artists found.</p>}

        {shouldDisplayAccordionContent &&
          artists.map((artist) => (
            <DetailsRow
              key={artist.artist_id}
              style={artist.end_date ? { backgroundColor: "var(--mono-grey-6, #F0F0F0)" } : undefined}
              details={[
                {
                  title: "Artist",
                  value: (
                    <ParticipantName name={artist.artist_name || "Unknown Artist"} participantId={artist.artist_id} />
                  ),
                },
                {
                  title: "",
                  value: <ParticipantStatusBadge status={artist.participant_status} />,
                  isBadge: true,
                },
                {
                  title: "Start Date",
                  value: (artist.start_date && formatDateStringLong(artist.start_date)) || "-",
                },
                {
                  title: "End Date",
                  value: (artist.end_date && formatDateStringLong(artist.end_date)) || "-",
                },
                ...(isVAOrMs && artist.end_date === null
                  ? [
                      {
                        title: "",
                        value: (
                          <IconLink src={PenWriteEditIcon} text="Edit" onClick={() => handleEditArtistClick(artist)} />
                        ),
                        isBadge: true,
                      },
                    ]
                  : []),
              ]}
              className="mt-4"
            />
          ))}
        <Row className="mt-3">
          <Pager page={page} totalPages={totalPages} totalItems={totalItems} setPage={setPage} />
        </Row>
      </CustomAccordion>
      {participantId && (
        <AddEditNewArtistModal
          show={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleArtistSubmit}
          EditingArtist={selectedArtist}
          participantId={participantId}
        />
      )}
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type={toastType} />
    </>
  );
};
